<template>
  <div class="modal fade" id="embed-builder-modal" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('embed_builder.modal.embed_map') }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('shared.close')"></button>
        </div>
        <div class="modal-body p-4">
          <div class="row g-4">
            <!-- Options column: grouped into tabs so the whole set isn't one long scroll -->
            <div class="col-lg-5">
              <div class="builder-tabs" role="tablist">
                <button
                  v-for="tab in tabs"
                  :key="tab.key"
                  type="button"
                  role="tab"
                  class="builder-tab"
                  :class="{ 'builder-tab--active': activeTab === tab.key }"
                  :id="`builderTab-${tab.key}`"
                  :aria-selected="activeTab === tab.key"
                  @click="activeTab = tab.key"
                >
                  {{ tab.label }}
                </button>
              </div>

              <div class="builder-panes">
                <!-- What the embed shows -->
                <div v-show="activeTab === 'content'" class="builder-pane" role="tabpanel">
                  <div class="setting" v-if="supportsHighlights">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" id="showHighlights" v-model="options.showHighlights">
                      <label class="form-check-label" for="showHighlights">
                        {{ $t('embed_builder.modal.show_highlights_pois') }}
                      </label>
                    </div>
                  </div>

                  <div class="setting">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" id="showElevation" v-model="options.showElevation">
                      <label class="form-check-label" for="showElevation">
                        {{ $t('embed_builder.modal.show_elevation_profile') }}
                      </label>
                    </div>
                    <div class="form-check form-switch setting__sub" v-show="options.showElevation">
                      <input class="form-check-input" type="checkbox" id="elevationMinimized" v-model="options.elevationMinimized">
                      <label class="form-check-label" for="elevationMinimized">
                        {{ $t('embed_builder.modal.start_minimized') }}
                      </label>
                    </div>
                  </div>

                  <div class="setting" v-if="supportsRoutePanels">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" id="showRoutesPanel" v-model="options.showRoutesPanel">
                      <label class="form-check-label" for="showRoutesPanel">
                        {{ $t('embed_builder.modal.show_routes_panel') }}
                      </label>
                    </div>
                    <div class="form-check form-switch setting__sub" v-show="options.showRoutesPanel">
                      <input class="form-check-input" type="checkbox" id="routesPanelCollapsed" v-model="options.routesPanelCollapsed">
                      <label class="form-check-label" for="routesPanelCollapsed">
                        {{ $t('embed_builder.modal.start_collapsed') }}
                      </label>
                    </div>
                  </div>

                  <div class="setting" v-if="offersStageMarkers">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" id="stageMarkers" v-model="options.stageMarkers">
                      <label class="form-check-label" for="stageMarkers">
                        {{ $t('embed_builder.modal.show_day_stops') }}
                      </label>
                    </div>
                    <div class="form-text">{{ $t('embed_builder.modal.show_day_stops_hint') }}</div>
                  </div>

                  <div class="setting">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" id="endpointLabels" v-model="options.endpointLabels">
                      <label class="form-check-label" for="endpointLabels">
                        {{ $t('embed_builder.modal.label_start_end_a_b') }}
                      </label>
                    </div>
                    <div class="form-check form-switch setting__sub" v-show="options.endpointLabels">
                      <input class="form-check-input" type="checkbox" id="endpointLabelCountry" v-model="options.endpointLabelCountry">
                      <label class="form-check-label" for="endpointLabelCountry">
                        {{ $t('embed_builder.modal.include_country') }}
                      </label>
                    </div>
                  </div>

                  <div class="setting">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" id="scrollZoom" v-model="options.scrollZoom">
                      <label class="form-check-label" for="scrollZoom">
                        {{ $t('embed_builder.modal.zoom_with_mouse_wheel') }}
                      </label>
                    </div>
                    <div class="form-text">{{ $t('embed_builder.modal.zoom_with_mouse_wheel_hint') }}</div>
                  </div>
                </div>

                <!-- How it looks -->
                <div v-show="activeTab === 'appearance'" class="builder-pane" role="tabpanel">
                  <div class="setting">
                    <h6 class="setting__title">{{ $t('embed_builder.modal.layout') }}</h6>
                    <div class="btn-group w-100" role="group">
                      <input type="radio" class="btn-check" id="layoutOverlay" value="overlay" v-model="options.layout">
                      <label class="btn btn-outline-light btn-sm" for="layoutOverlay">{{ $t('embed_builder.modal.layout_overlay') }}</label>
                      <input type="radio" class="btn-check" id="layoutStacked" value="stacked" v-model="options.layout">
                      <label class="btn btn-outline-light btn-sm" for="layoutStacked">{{ $t('embed_builder.modal.layout_stacked') }}</label>
                    </div>
                    <div class="form-text">{{ $t('embed_builder.modal.layout_stacked_hint') }}</div>
                  </div>

                  <div class="setting">
                    <h6 class="setting__title">{{ $t('embed_builder.modal.theme') }}</h6>
                    <div class="btn-group w-100" role="group">
                      <input type="radio" class="btn-check" id="themeDark" value="dark" v-model="options.theme">
                      <label class="btn btn-outline-light btn-sm" for="themeDark">{{ $t('embed_builder.modal.theme_dark') }}</label>
                      <input type="radio" class="btn-check" id="themeLight" value="light" v-model="options.theme">
                      <label class="btn btn-outline-light btn-sm" for="themeLight">{{ $t('embed_builder.modal.theme_light') }}</label>
                    </div>
                    <div class="form-text">{{ $t('embed_builder.modal.theme_hint') }}</div>
                  </div>

                  <div class="setting">
                    <h6 class="setting__title">{{ $t('embed_builder.modal.units') }}</h6>
                    <div class="btn-group w-100" role="group">
                      <input type="radio" class="btn-check" id="unitsMetric" value="metric" v-model="options.units">
                      <label class="btn btn-outline-light btn-sm" for="unitsMetric">{{ $t('embed_builder.modal.km_m') }}</label>
                      <input type="radio" class="btn-check" id="unitsImperial" value="imperial" v-model="options.units">
                      <label class="btn btn-outline-light btn-sm" for="unitsImperial">{{ $t('embed_builder.modal.miles_ft') }}</label>
                    </div>
                  </div>
                </div>

                <!-- The same colour / thickness / opacity controls as the map's Customise route style -->
                <div v-show="activeTab === 'route'" class="builder-pane" role="tabpanel">
                  <div class="form-text route-style-hint">{{ $t('embed_builder.modal.route_style_hint') }}</div>

                  <div v-if="routeTypes.length > 1" class="route-type-tabs">
                    <button
                      v-for="type in routeTypes"
                      :key="type"
                      type="button"
                      class="route-type-tab"
                      :class="{ 'route-type-tab--active': activeRouteType === type }"
                      :id="`routeType-${type}`"
                      @click="activeRouteType = type"
                    >
                      {{ routeTypeLabel(type) }}
                    </button>
                  </div>

                  <LineStyleEditor
                    :color="activeRouteStyle.color"
                    :thickness="activeRouteStyle.thickness"
                    :opacity="activeRouteStyle.opacity"
                    @update:color="setRouteStyle({ color: $event })"
                    @update:thickness="setRouteStyle({ thickness: $event })"
                    @update:opacity="setRouteStyle({ opacity: $event })"
                    @reset="resetRouteStyle"
                  >
                    <button type="button" class="match-map-btn" id="routeStyleMatchMap" @click="matchMapRouteStyle">
                      {{ $t('embed_builder.modal.route_style_match_map') }}
                    </button>
                  </LineStyleEditor>
                </div>
              </div>
            </div>

            <!-- Preview, size and code -->
            <div class="col-lg-7">
              <h6 class="mb-2">{{ $t('shared.preview') }}</h6>
              <div class="embed-preview-container">
                <div v-if="iframeLoading" class="iframe-loading">
                  <div class="spinner"></div>
                  <span>{{ $t('embed_builder.modal.loading_preview') }}</span>
                </div>
                <!-- Only once the modal has opened: the preview is the full embed page, with its own
                     Mapbox map (tiles, fonts and a billable map load), and the modal is mounted on
                     every hike and recording page whether or not anyone opens it. -->
                <iframe
                  v-if="opened"
                  :src="embedUrl"
                  :width="previewWidth"
                  :height="previewHeight"
                  frameborder="0"
                  allowfullscreen
                  class="embed-preview"
                  :class="{ 'iframe-hidden': iframeLoading }"
                  @load="onIframeLoad"
                ></iframe>
              </div>

              <div class="size-row">
                <h6 class="size-row__title">{{ $t('embed_builder.modal.embed_size') }}</h6>
                <label class="size-field">
                  <span>{{ $t('embed_builder.modal.width') }}</span>
                  <input type="text" class="form-control form-control-sm rounded-pill py-2 px-3" v-model="dimensions.width" placeholder="100%">
                </label>
                <label class="size-field">
                  <span>{{ $t('embed_builder.modal.height') }}</span>
                  <input type="text" class="form-control form-control-sm rounded-pill py-2 px-3" v-model="dimensions.height" placeholder="500">
                </label>
              </div>

              <div class="card p-3 mt-3">
                <div class="d-flex justify-content-between align-items-start gap-3">
                  <div>
                    <h6 class="mb-1">{{ $t('embed_builder.modal.embed_code') }}</h6>
                    <p class="embed-code-hint mb-2">{{ $t('embed_builder.modal.copy_and_paste_this_code_into') }}</p>
                  </div>
                  <button class="btn btn-small btn-seconday flex-shrink-0" type="button" @click="copyCode">
                    <span class="material-icons" style="font-size: 18px;">content_copy</span>
                    {{ copied ? $t('common.copied') : $t('common.copy') }}
                  </button>
                </div>
                <textarea
                  class="form-control embed-code-textarea"
                  :value="embedCode"
                  readonly
                  rows="3"
                  ref="codeTextarea"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('shared.close') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import copyToClipboard from '../../utils/helpers/CopyToClipboardHelper';
import {
  EmbedLayout,
  EmbedMode,
  EmbedTheme,
  embedIframeCode,
  embedUrl,
  embedWidthAttribute,
  embedHeightAttribute,
  previewHeight,
  supportsHighlights,
  supportsRoutePanels,
  supportsStageMarkers,
} from '../../utils/embeds/embedUrlParams';
import LineStyleEditor from '../base_map_dialog/components/LineStyleEditor.vue';
import {
  ownerRouteStyles,
  type EmbedRouteType,
  type EmbedRouteStyles,
} from '../../utils/embeds/embedRouteStyle';
import { DEFAULT_ROUTE_STYLE_SETTINGS, type RouteTypePrefs } from '../../utils/routeStyleSettings';

type BuilderTab = 'content' | 'appearance' | 'route';

/** The route types a mode draws: a recording is one track; a hike has main, alternative and spur routes. */
function routeTypesForMode(mode: EmbedMode): EmbedRouteType[] {
  return mode === 'recording' ? ['recording'] : ['main', 'alt', 'spur'];
}

declare global {
  interface Window {
    embedBuilderConfig?: {
      hikeId?: string;
      recordingId?: string;
      embedBaseUrl: string;
      isMultiDay?: boolean;
      // Absent on the hike pages that predate recordings embeds — they mean 'hike'.
      mode?: EmbedMode;
    };
  }
}

export default defineComponent({
  name: 'EmbedBuilderModal',
  components: { LineStyleEditor },
  data() {
    const mode: EmbedMode = window.embedBuilderConfig?.mode || 'hike';
    return {
      activeTab: 'content' as BuilderTab,
      activeRouteType: routeTypesForMode(mode)[0],
      options: {
        showHighlights: true,
        showElevation: true,
        elevationMinimized: true,
        showRoutesPanel: true,
        routesPanelCollapsed: true,
        units: 'metric',
        layout: 'overlay' as EmbedLayout,
        scrollZoom: true,
        endpointLabels: false,
        endpointLabelCountry: false,
        // On for a multi-day hike, so the embed numbers its days as the hike map does.
        stageMarkers: supportsStageMarkers(mode) && !!window.embedBuilderConfig?.isMultiDay,
        theme: 'dark' as EmbedTheme,
        // Starts as the route style the owner uses on their own map, so the embed matches it.
        routeStyles: ownerRouteStyles() as EmbedRouteStyles
      },
      dimensions: {
        width: '100%',
        height: '400'
      },
      copied: false,
      iframeLoading: true,
      opened: false
    };
  },
  computed: {
    mode(): EmbedMode {
      return window.embedBuilderConfig?.mode || 'hike';
    },

    tabs(): Array<{ key: BuilderTab; label: string }> {
      return [
        { key: 'content', label: this.$t('embed_builder.modal.tab_content') },
        { key: 'appearance', label: this.$t('embed_builder.modal.tab_appearance') },
        { key: 'route', label: this.$t('embed_builder.modal.tab_route_line') },
      ];
    },

    routeTypes(): EmbedRouteType[] {
      return routeTypesForMode(this.mode);
    },

    activeRouteStyle(): RouteTypePrefs {
      return this.options.routeStyles[this.activeRouteType];
    },

    /** A recording has no POI highlights and no routes panel to configure. */
    supportsHighlights(): boolean {
      return supportsHighlights(this.mode);
    },

    supportsRoutePanels(): boolean {
      return supportsRoutePanels(this.mode);
    },

    /** Only a multi-day hike has day boundaries to number. */
    offersStageMarkers(): boolean {
      return supportsStageMarkers(this.mode) && !!window.embedBuilderConfig?.isMultiDay;
    },

    embedBaseUrl(): string {
      return window.embedBuilderConfig?.embedBaseUrl || '';
    },

    embedUrl(): string {
      return embedUrl(this.embedBaseUrl, this.options, this.mode);
    },

    previewWidth(): string {
      // For preview, use 100% of container
      return '100%';
    },

    previewHeight(): string {
      return previewHeight(this.dimensions.height);
    },

    embedWidth(): string {
      return embedWidthAttribute(this.dimensions.width);
    },

    embedHeight(): string {
      return embedHeightAttribute(this.dimensions.height);
    },

    embedCode(): string {
      return embedIframeCode(this.embedUrl, this.dimensions.width, this.dimensions.height);
    }
  },
  mounted() {
    this.$el.addEventListener('show.bs.modal', this.onModalShow);
  },
  beforeUnmount() {
    this.$el.removeEventListener('show.bs.modal', this.onModalShow);
  },
  watch: {
    embedUrl() {
      // Show loading when URL changes (options toggled)
      this.iframeLoading = true;
    }
  },
  methods: {
    routeTypeLabel(type: EmbedRouteType): string {
      // Spelled out, not built from `type`: the client bundle only carries keys it can see.
      switch (type) {
        case 'main': return this.$t('base_map_dialog.customise_route_style_view.main');
        case 'alt': return this.$t('base_map_dialog.customise_route_style_view.alt');
        case 'spur': return this.$t('base_map_dialog.customise_route_style_view.spur');
        default: return this.$t('shared.recording');
      }
    },
    setRouteStyle(patch: Partial<RouteTypePrefs>) {
      const type = this.activeRouteType;
      this.options.routeStyles = {
        ...this.options.routeStyles,
        [type]: { ...this.options.routeStyles[type], ...patch },
      };
    },
    /** The map dialog's "Reset to default": this route type back to the app's own style. */
    resetRouteStyle() {
      const type = this.activeRouteType;
      this.options.routeStyles = {
        ...this.options.routeStyles,
        [type]: { ...DEFAULT_ROUTE_STYLE_SETTINGS[type] },
      };
    },
    /** Back to what the owner has saved for this route type on their map. */
    matchMapRouteStyle() {
      const type = this.activeRouteType;
      this.options.routeStyles = { ...this.options.routeStyles, [type]: { ...ownerRouteStyles()[type] } };
    },
    onModalShow() {
      this.opened = true;
    },
    onIframeLoad() {
      this.iframeLoading = false;
    },
    copyCode() {
      copyToClipboard(this.embedCode, (success, error) => {
        if (error) {
          alert(this.$t('embed_builder.modal.failed_to_copy_code'));
          return;
        }
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      });
    }
  }
});
</script>

<style scoped>
/* Options column */
.builder-tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 9999px;
}

.builder-tab {
  flex: 1;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  border-radius: 9999px;
  padding: 7px 8px;
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.builder-tab:hover {
  color: #fff;
}

.builder-tab--active,
.builder-tab--active:hover {
  background: #fddf00;
  color: #17181d;
}

/* Tall option lists scroll inside the column, so the preview and code stay in view. */
.builder-panes {
  max-height: min(640px, calc(100vh - 220px));
  overflow-y: auto;
  /* A scrolling box also clips sideways, so give the selected swatch's ring (and the focus
     outlines) room inside it, and pull the box out by the same amount to keep the content
     lined up with the tabs. */
  padding: 4px 8px 4px 4px;
  margin: -4px -8px -4px -4px;
}

.setting {
  padding: 12px 14px;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
}

.setting .form-check {
  margin-bottom: 0;
}

.setting__sub {
  margin-top: 10px;
  margin-left: 0.5rem;
}

.setting__title {
  margin: 0 0 10px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
}

/* Selected segment matches the active tab */
.btn-check:checked + .btn-outline-light {
  background: #fddf00;
  border-color: #fddf00;
  color: #17181d;
}

/* Bootstrap's grey helper text is unreadable on this dark dialog. */
.form-text {
  margin-top: 8px;
  font-size: 0.78rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.7);
}

.route-style-hint {
  margin: 0 0 12px;
}

.route-type-tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 9999px;
}

.route-type-tab {
  flex: 1;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  border-radius: 9999px;
  padding: 6px 0;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.route-type-tab--active {
  background: #fddf00;
  color: #17181d;
}

.match-map-btn {
  display: block;
  width: 100%;
  margin-top: 8px;
  padding: 8px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 9999px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.82rem;
  cursor: pointer;
}

.match-map-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

/* Size sits under the preview, on one row */
.size-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-top: 12px;
}

.size-row__title {
  flex: 0 0 auto;
  margin: 0 8px 8px 0;
}

.size-field {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
}

.size-field span {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.7);
}

.embed-code-hint {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
}

.embed-preview-container {
  background: #2a2d35;
  border-radius: 8px;
  padding: 8px;
  min-height: 200px;
  position: relative;
}

.embed-preview {
  border-radius: 6px;
  width: 100%;
  background: #1a1b21;
  transition: opacity 0.2s ease;
}

.embed-preview.iframe-hidden {
  opacity: 0;
}

.iframe-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  background: #1a1b21;
  border-radius: 6px;
  margin: 8px;
}

.iframe-loading .spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 223, 70, 0.2);
  border-top-color: #FFDF46;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.embed-code-section {
  border-radius: 8px;
  padding: 16px;
}

.embed-code-section h6 {
  color: #fff;
}

.embed-code-section p {
  color: rgba(255, 255, 255, 0.6);
}

.embed-code-textarea {
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  font-size: 11px;
  background: #1a1b21 !important;
  color: #e0e0e0 !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  border-radius: 6px !important;
  resize: none;
  padding: 12px;
  margin-bottom: 12px;
}

.embed-code-textarea:focus {
  background: #1a1b21 !important;
  color: #e0e0e0 !important;
  border-color: #FFDF46 !important;
  box-shadow: none !important;
}

.btn-copy {
  width: 100%;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  padding: 12px 16px;
}

/* Switch toggle styling */
.form-check-input {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
}

.form-check-input:checked {
  background-color: #FFDF46;
  border-color: #FFDF46;
}

/* Ensure the circle/knob has high contrast */
.form-check-input[type="checkbox"] {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23000'/%3e%3c/svg%3e");
}

.form-check-input[type="checkbox"]:checked {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23000'/%3e%3c/svg%3e");
}

.form-check-input:focus {
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: none;
  outline: none;
}

.form-check-input:checked:focus {
  border-color: #FFDF46;
  box-shadow: none;
  outline: none;
}

.btn-primary {
  background-color: #FFDF46;
  border-color: #FFDF46;
  color: #000;
  font-weight: 500;
}

.btn-primary:hover {
  background-color: #e6c93f;
  border-color: #e6c93f;
  color: #000;
}

.modal-content {
  border-radius: 12px;
}

.modal-header {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.modal-footer {
  border-top-color: rgba(255, 255, 255, 0.1);
}
</style>
